<script lang="ts">
	import Header from '#blocks/header.svelte'
	import Render from '#components/render.svelte'
	import Image from '#components/image.svelte'
	import Quotes from '#blocks/quotes.svelte'
	import Shape from '#components/shape.svelte'
	import Linklist from '#blocks/linklist.svelte'

	import type { Member } from '#library/types.ts'
	import type { Storyblok } from '#library/storyblok.svelte.ts'
	import type { ComponentProps } from 'svelte'

	interface Props {
		data: Storyblok<Member>
	}

	let { data }: Props = $props()
	const content = $derived(data.story?.content)
</script>

<Header />

{#if content}
	<section class="intro contain theme-crocus-on-light-gimson">
		<div class="body">
			<div class="title">{content.title}</div>
			{#if content.education?.content?.[0].content}
				<br />
				<div class="subtitle">Education</div>
				<Render content={content.education} />
			{/if}
		</div>
		{#if content.photo?.filename}
			<figure class="figure">
				<Image src={content.photo.filename} alt={content.photo.alt} focus={content.photo.focus} />
			</figure>
		{/if}
	</section>

	{#if content.quote}
		<Quotes
			blok={{
				items: [{ title: content.quote }],
				theme: 'theme-lupton-on-pink'
			} as unknown as ComponentProps<typeof Quotes>['blok']}
		/>
	{/if}

	<section class="interview contain">
		<div>
			{#if content.interview1}
				<Render content={content.interview1} />
			{/if}
			{#if content.interview2 && !content.passions?.length}
				<Render content={content.interview2} />
			{/if}
		</div>
		<figure class="shape">
			{#if content.image?.filename}
				<Shape src={content.image.filename} alt={content.image.alt} focus={content.image.focus} />
			{/if}
		</figure>
	</section>

	{#if content.passions?.length}
		<Quotes
			blok={{
				items: content.passions,
				theme: 'theme-canopy-on-forget-me-not',
				nomark: false,
				padding_top: '64',
				padding_bottom: '64'
			} as unknown as ComponentProps<typeof Quotes>['blok']}
		/>
		<section class="interview contain">
			<div>
				{#if content.interview2}
					<Render content={content.interview2} />
				{/if}
			</div>
		</section>
	{/if}
{/if}
<Linklist
	blok={{
		theme: 'theme-rose-on-white',
		title: 'Discover what we stand for',
		body: 'Bedales champions an innovative student-centred education, prioritising academic freedom, creativity, sustainability and wellbeing. We foster individuality, moral purpose and a lifelong love of learning in a changing world. ',
		items: [
			{
				link: {
					type: 'internal',
					text: 'Learning',
					url: '/learning'
				}
			},
			{
				link: {
					type: 'internal',
					text: 'Living',
					url: '/living'
				}
			},
			{
				link: {
					type: 'internal',
					text: 'Community',
					url: '/community'
				}
			},
			{
				link: {
					type: 'internal',
					text: 'Environment',
					url: '/environment'
				}
			}
		]
	}}
/>

<style lang="css">
	.intro {
		background: var(--c-background);
		color: var(--c-midground);
		display: grid;
		gap: 3rem;
		padding-block: 0 var(--s-104);
		overflow: visible;
		z-index: 1;

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}

		+ .interview {
			padding-block-start: 0;
		}
	}

	.title {
		font: 500 var(--f-h4);
	}

	.subtitle {
		font: 500 var(--f-h5);
	}

	.body {
		font: 400 var(--f-body1);
	}

	.figure {
		@media (--from-mobile) {
			margin-block-start: -6rem;
		}
	}

	.interview {
		display: grid;
		gap: 3rem;
		padding-block: var(--s-104);

		@media (--from-tablet) {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		}
	}

	.shape {
		aspect-ratio: 510/485;
		display: flex;
		justify-content: center;
		max-height: 32rem;
		position: sticky;
		top: 6rem;
	}
</style>
